<script setup>
import DateRangePicker from '@js/components/pickers/DateRangePicker.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, facebookAdsProductFeed, facebookAdsProductFeedVariables } =
    defineProps({
        tenant: {
            type: Object,
            required: true,
        },
        facebookAdsProductFeed: {
            type: Object,
            required: true,
        },
        facebookAdsProductFeedVariables: {
            type: Array,
            required: false,
            default: () => [],
        },
    });

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'e_commerce_products.cms_product_id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID<br/>Produit',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.cmsProductId,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_products.sku',
            header: () => 'SKU',
            cell: (props) =>
                props.row.original.sku
                    ? h(TextCell, {
                          text: props.row.original.sku,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_product_languages.name',
            header: () => 'Nom',
            cell: (props) =>
                props.row.original.name && props.row.original.url
                    ? h(LinkCell, {
                          text: props.row.original.name,
                          href: props.row.original.url,
                          target: '_blank',
                          length: 160,
                      })
                    : props.row.original.name
                      ? h(TextCell, {
                            text: props.row.original.name,
                            length: 160,
                        })
                      : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_products.final_price_excl_tax',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Prix<br/>Produit HT',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.finalPriceExclTax,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_products.final_price_incl_tax',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Prix<br/>Produit TTC',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.finalPriceInclTax,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'e_commerce_products.cost',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût<br/>Produit HT',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.cost,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'real_quantity',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Stock',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.quantity,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'quantity_sold',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nb<br/>ventes',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.quantitySold,
                }),
        }),
        ...facebookAdsProductFeedVariables.map((variable) =>
            columnHelper.accessor(() => null, {
                id: `variable_${variable.value}`,
                headerCustom: true,
                header: (props) =>
                    h(HeaderCell, {
                        header: props.header,
                        title: variable.label,
                    }),
                cell: (props) =>
                    h(StatusCell, {
                        status: !!(
                            props.row.original.variables &&
                            props.row.original.variables[variable.value]
                        ),
                    }),
            }),
        ),
    ],
    url: route('front.projectManagement.sma.postProductFeedItems', {
        tenant: tenant.id,
        facebookAdsProductFeed: facebookAdsProductFeed.id,
    }),
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
        force-scroll
    >
        <template #actions>
            <DateRangePicker :callback="updateData" />
        </template>
    </DataTableLayout>
</template>
